import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  MenuItem
} from "@mui/material";

interface AbonoDialogProps {
  open: boolean;
  onClose: () => void;
  workerName?: string;
  currentDebt: number;
  onSubmit: (amount: number, paymentMethod: string) => Promise<void>;
}

export default function AbonoDialog({ open, onClose, workerName, currentDebt, onSubmit }: AbonoDialogProps) {
  const [amountRaw, setAmountRaw] = useState<number | "">("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setAmountRaw("");
      setPaymentMethod("efectivo");
      setError("");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const val = amountRaw === "" ? 0 : amountRaw;
    
    if (val > currentDebt) {
      setError(`El abono ($${val.toLocaleString()}) no puede exceder la deuda actual ($${currentDebt.toLocaleString()}).`);
      return;
    }
    
    setError("");
    if (!val || val <= 0 || !paymentMethod) return;
    
    setLoading(true);
    try {
      await onSubmit(val, paymentMethod);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>
            Abonar Deuda
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {workerName} - Deuda actual: ${currentDebt.toLocaleString()}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} pt={1}>
            <TextField
              label="Monto a Abonar"
              type="text"
              value={amountRaw !== "" ? amountRaw.toLocaleString() : ""}
              fullWidth
              size="small"
              onChange={(e) => {
                const rawStr = e.target.value.replace(/\D/g, "");
                const val = rawStr ? Number(rawStr) : "";
                setAmountRaw(val);
                if (val !== "" && val > currentDebt) {
                  setError(`El abono ($${val.toLocaleString()}) no puede exceder la deuda actual ($${currentDebt.toLocaleString()}).`);
                } else {
                  setError("");
                }
              }}
              error={!!error}
              helperText={error || `Máximo: $${currentDebt.toLocaleString()}`}
            />
            <TextField
              select
              label="Método de Pago"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              required
              fullWidth
              size="small"
            >
              <MenuItem value="efectivo">💵 Efectivo</MenuItem>
              <MenuItem value="tarjeta">💳 Tarjeta de Crédito/Débito</MenuItem>
              <MenuItem value="transferencia">🏦 Transferencia / Nequi</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} color="inherit">Cancelar</Button>
          <Button type="submit" variant="contained" color="success" disabled={loading || !!error || amountRaw === "" || amountRaw > currentDebt}>
            {loading ? "Procesando..." : "Confirmar Abono"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
